import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useGetDatasetDataMutation } from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ChevLeftIcon, CloseIcon } from "@/assets/icons";
import AdminAreaSelection from "@/components/adminAreaSelection";
import Divider from "@/components/ui/Divider";
import { MAX_POI } from "@/constants/dataset";
import {
  getAdminAreaRequired,
  getAdminAreaVisibility
} from "@/helpers/datasetAttributeHelper";
import { useAttributeFilter } from "@/hooks/attributeFilter/useAttributeFilter";
import { useH3Configuration } from "@/hooks/h3Configuration/useH3Configuration";
import { useDataset } from "@/hooks/map/useDataset";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { useDatasetStream } from "@/hooks/useDatasetStream";
import { RootState } from "@/store";
import { selectDatasetFilters } from "@/store/dataset/selectors";
import { updateFilters } from "@/store/slice/datasetSlice";
import { removeLinkedDatasetId } from "@/store/slice/globalFilterSlice";
import { setLegendFilters } from "@/store/slice/mapSlice";
import { setDatasetConfigTemp } from "@/store/slice/spatialSlice";
import {
  Dataset,
  DatasetDetailResponse,
  DatasetFilter
} from "@/types/dataset.ts";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import AggregationFilter from "./AggregationFilter";
import { AttributeFilter } from "./AttributeFilter";
import EmptyState from "./EmptyState";
import SavedFiltersList from "./SavedFiltersList";

const TEST_ID_PREFIX = "attribute-filter-popup-content";

type Props = {
  disabled?: boolean;
  onClose: () => void;
  dataset: Dataset;
  selectedDataset: DatasetDetailResponse | null;
};

const CatchmentErrorMessage = ({ datasetLength = 0 }) => (
  <span className="text-cinnabar text-xs mb-2">
    {datasetLength === 0
      ? "No data selected. Please select at least one record to proceed."
      : `Selection exceeds the limit (${datasetLength}/${MAX_POI}). Please reduce the number of records to proceed.`}
  </span>
);

const EditPopupAttribute = ({
  disabled = false,
  onClose,
  dataset,
  selectedDataset
}: Props) => {
  const dispatch = useDispatch();
  const datasetId = dataset.data.id;
  const datasetFiltersDatex: DatasetFilter[] = useSelector((state: RootState) =>
    selectDatasetFilters(datasetId)(state)
  );
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const catchmentDataset = catchmentSettingsTemp?.datasetConfig?.datasets;
  const { legendFilters } = useSelector((state: RootState) => state.map);

  const { linkedDatasetIds } = useSelector(
    (state: RootState) => state.globalFilter
  );
  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);
  const datasetFilters = useMemo(() => {
    return showCatchmentPopup
      ? catchmentDataset?.[datasetId ?? ""]?.filters || []
      : datasetFiltersDatex;
  }, [showCatchmentPopup, catchmentDataset, datasetId, datasetFiltersDatex]);

  const [datasetStreamId, setDatasetStreamId] = useState<string | undefined>();
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const [isOverrideAdminArea, setIsOverrideAdminArea] = useState(false);

  const { handleUpdateDatasetData } = useDataset();
  const { clearLoadingState, showLoading, showError } = useLoadingAndToast();
  const { data: userProfile } = useGetUserProfileAPI();
  const { h3Configuration, setH3Configuration } = useH3Configuration({
    dataset,
    selectedDataset
  });
  const {
    isGettingAttributeValues,
    attributeValues,
    defaultSelectedValue,
    filterOptions,
    selectedOptions,
    savedFilters,
    showFilters,
    initialAdminArea,
    isAttributeFilterValid,
    selectAttributeItem,
    searchTerm,
    setShowFilters,
    handleAddAttributeFilter,
    getAvailableFilterOptions,
    handleSaveFilter,
    handleEditFilter,
    handleDeleteFilter,
    handleSearchChange,
    handleSelectFilter,
    handleAdminAreaChange,
    onFilterChange,
    selectedAdminArea,
    setSelectedAdminArea,
    adminAreaValid,
    isAdminAreaSelection,
    isAdminAreaSelectionValid,
    showErrorPOI,
    setShowErrorPOI
  } = useAttributeFilter({
    selectedDataset,
    initialDatasetFilters: datasetFilters,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    timezone: userProfile?.timezone as any
  });

  const {
    mutateAsync: getDatasetDataPost,
    data: datasetData,
    isPending: isPendingGetDatasetData
  } = useGetDatasetDataMutation({
    timezone: userProfile?.timezone,
    options: {
      onMutate: () => {
        if (!showCatchmentPopup) {
          showLoading();
        }
      },
      onSuccess: () => {
        clearLoadingState();
      },
      onError: () => {
        showError();
      }
    }
  });

  useDatasetStream({
    datasetId: datasetStreamId,
    enabled: !!datasetStreamId,
    timezone: userProfile?.timezone,
    filters: savedFilters,
    h3Configuration,
    onOpen: () => {
      showLoading();
      setIsLoadingDataset(true);
    },
    onStreamComplete: (data) => {
      const currentDatasetData = data.rawDataset;

      if (showCatchmentPopup && currentDatasetData.rows.length <= MAX_POI) {
        dispatch(
          setDatasetConfigTemp({
            uniqueId: datasetId,
            rawDataset: currentDatasetData,
            filters: savedFilters
          })
        );
      } else {
        // Regular mode - update dataset data and filters
        handleUpdateDatasetData(datasetId, currentDatasetData, h3Configuration);
        dispatch(
          updateFilters({
            datasetId,
            filters: savedFilters
          })
        );
        dispatch(
          setLegendFilters({
            ...legendFilters,
            [datasetId]: []
          })
        );
      }
      // Close only if we're not just in showFilters mode
      onClose();
      setDatasetStreamId(undefined);
      setIsLoadingDataset(false);
      clearLoadingState();
    },
    onError: () => {
      setDatasetStreamId(undefined);
      setIsLoadingDataset(false);
      showError();
    }
  });

  const onSaveFilter = async () => {
    if (!selectedDataset) return;

    // First handle any needed filter saving
    const newFilter: DatasetFilter[] = showFilters
      ? ((await handleSaveFilter()) ?? savedFilters)
      : savedFilters;

    if (!showFilters) {
      if (datasetData) {
        dispatch(
          setDatasetConfigTemp({
            uniqueId: datasetId,
            rawDataset: datasetData,
            filters: savedFilters
          })
        );
        onClose();
      } else {
        if (isOverrideAdminArea && linkedDatasetIds.includes(datasetId)) {
          dispatch(
            removeLinkedDatasetId({
              datasetId
            })
          );
        }
        setDatasetStreamId(selectedDataset.id);
      }
    }

    if (showCatchmentPopup && showFilters) {
      // handle validation for dataset on catchment
      const currentDatasetData = await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });

      if ((currentDatasetData?.rows?.length ?? 0) > MAX_POI) {
        setShowErrorPOI(true);
      }
    }
  };

  const onDeleteFilter = async (filter: DatasetFilter) => {
    const newFilter = handleDeleteFilter(filter);
    if (selectedDataset && showCatchmentPopup) {
      await getDatasetDataPost({
        id: selectedDataset.id,
        filter: newFilter,
        h3Configuration
      });
    }
  };

  const isInvalidPOILength = useMemo(
    () =>
      !!showCatchmentPopup &&
      ((datasetData?.rows?.length ?? 0) === 0 ||
        (datasetData?.rows?.length ?? 0) > MAX_POI),
    [showCatchmentPopup, datasetData?.rows?.length]
  );

  const showErrorPOICondition = useMemo(
    () =>
      showCatchmentPopup &&
      showErrorPOI &&
      selectedDataset &&
      isInvalidPOILength,
    [showCatchmentPopup, showErrorPOI, selectedDataset, isInvalidPOILength]
  );

  useEffect(() => {
    // validation when back
    if (showCatchmentPopup && !showFilters && datasetData) {
      setShowErrorPOI(isInvalidPOILength);
    }
  }, [showCatchmentPopup, showFilters, datasetData, isInvalidPOILength]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="flex flex-row justify-between items-start mb-4">
        <div>
          <div className="flex flex-row items-center gap-2">
            {showFilters && (
              <ChevLeftIcon
                data-testid="chev-left-icon"
                className={twMerge("w-4 h-4 cursor-pointer")}
                onClick={() => setShowFilters(false)}
              />
            )}
            <h2 className="text-txtHeading font-bold text-[20px]">
              {showFilters ? "Edit Attribute Filter" : "Attribute Filter"}
            </h2>
          </div>

          {!showFilters && (
            <p className="text-txtBody text-[10px] mt-2">
              You may add filters to the attributes based on the dataset you
              select.
            </p>
          )}
        </div>

        <button
          className="bg-transparent border-0 p-0"
          onClick={() => {
            setIsOverrideAdminArea(false);
            onClose();
          }}
          data-testid="close-button"
        >
          <CloseIcon className="fill-dim-gray" />
        </button>
      </div>
      <div className="font-sans w-full h-full rounded-[12px] overflow-y-visible">
        <div className="flex flex-col min-h-0">
          {showFilters ? (
            <AttributeFilter
              isEditMode
              savedFilters={savedFilters}
              getAvailableFilterOptions={getAvailableFilterOptions}
              handleSelectFilter={handleSelectFilter}
              selectAttributeItem={selectAttributeItem}
              defaultSelectedValue={defaultSelectedValue}
              attributeValues={attributeValues}
              selectedOptions={selectedOptions}
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              isGettingAttributeValues={isGettingAttributeValues}
              onChange={onFilterChange}
            />
          ) : (
            <>
              {isAdminAreaSelection && (
                <AdminAreaSelection
                  selectedData={selectedAdminArea || initialAdminArea}
                  onSelectData={(newAdminArea) => {
                    setSelectedAdminArea(newAdminArea);
                    handleAdminAreaChange(newAdminArea);
                    setIsOverrideAdminArea(true);
                  }}
                  options={{
                    showCountryLevel: false,
                    showLabelAboveInput: true,
                    showLabel: false,
                    prefixLabel: "Select",
                    required: getAdminAreaRequired(selectedDataset),
                    visible: getAdminAreaVisibility(selectedDataset)
                  }}
                />
              )}
              <AggregationFilter
                value={h3Configuration}
                spatialType={selectedDataset?.spatial_aggregation_type}
                dataSource={selectedDataset?.source}
                onChangeAggregationType={(value) => {
                  setH3Configuration((prev) => ({
                    ...prev,
                    aggregationType: value
                  }));
                }}
              />
              {showErrorPOICondition && (
                <CatchmentErrorMessage
                  datasetLength={datasetData?.rows?.length}
                />
              )}
              <div className="flex-1 flex flex-col">
                <Divider
                  className={twMerge(isAdminAreaSelection ? "my-3" : "mb-3")}
                />
                {savedFilters?.length > 0 ? (
                  <SavedFiltersList
                    filterOptions={filterOptions}
                    filters={savedFilters}
                    onEdit={handleEditFilter}
                    onDelete={onDeleteFilter}
                  />
                ) : (
                  !isAdminAreaSelection && <EmptyState />
                )}
              </div>
            </>
          )}
        </div>

        <Button
          disabled={
            disabled ||
            savedFilters?.length === filterOptions.length ||
            !adminAreaValid
          }
          className="h-[40px] mt-3 w-full border-teal-500 bg-white text-teal-500 border text-sm font-bold disabled:border-gainsboro disabled:border disabled:text-gainsboro"
          onClick={handleAddAttributeFilter}
          data-testid={`${TEST_ID_PREFIX}-add-attribute-filter-button`}
        >
          Add Attribute Filter
        </Button>

        <Button
          className="h-[40px] w-full btn-primary mt-4 text-sm font-bold"
          disabled={
            (showFilters
              ? !isAttributeFilterValid
              : !isAdminAreaSelectionValid) ||
            showErrorPOI ||
            isPendingGetDatasetData ||
            isLoadingDataset
          }
          onClick={onSaveFilter}
          data-testid={`${TEST_ID_PREFIX}-save-button`}
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default EditPopupAttribute;
